import { NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import {
  Button,
  QuoteProceessUrlPath,
  apiQueryQuote,
  apiQueryRemainingResponse,
} from '@jove-frontend/uikit';
import { t } from 'i18next';
import produce from 'immer';
import { GlobalState, useStateMachine } from 'little-state-machine';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import Balancer from 'react-wrap-balancer';
import Globe from '../../assets/Globe_with_swooshes-full.jpg';
import Header from '../../components/Header';
import { ProgressBar } from '../../components/StepsProgress';
import { AppRoutesPaths } from '../../types';
import { updateAction } from '../../util/update-action';

export default function QuotedPage() {
  const { state, actions } = useStateMachine({ updateAction });
  const router = useRouter();
  const { quoteId } = router.query;

  const insertFormDataToLocal = (data: any, quotedData: any) => {
    try {
      const { responses } = data;
      const quotedInfo = quotedData.payload;
      const payload = {
        ...state,
        quoteId: quoteId as string,
        stages: data.stages,
        version: data.version,
        responses: data.responses,
        providerCode: data.providerCode,
        skipTech: data.skipTech,
        price: quotedInfo.price,
        prices: quotedInfo.prices,
        documents: quotedInfo.documents,
        business: {
          ...state.business,
          type: responses.businessType || '',
          policyType: responses.policyType,
          postcode: responses.postcode,
          activity: responses.activity || '',
          involvedActivities: responses.involvedActivities || [],
          turnover: responses.turnover,
          turnoverRaw: responses.turnoverRaw,
          companyName: responses.companyName,
          turnoverSplit: responses.incomeOrigin,
          number: responses.number,
          businessAddress: responses.businessAddress,
          city: responses.city || '',
          address2: responses.address2,
          streetAddress: responses.streetAddress,
          businessPostcode: responses.businessPostcode,
          businessCity: responses.businessCity,
          businessCountry: responses.businessCountry,
          country: responses.country || responses.businessCount,
          businessActivity: responses.businessActivity,
          companyNumber: responses.companyNumber,
          employees: responses.employees,
          covers: responses.covers,
          prices: responses.prices,
          employeesSplit: responses.employeesSplit || {
            clerical: 0,
            manual: 0,
          },
        },
        covers: responses.covers,
        company: {
          ...state.company,
          name: responses.companyName || '',
          registration_num: responses.companyNumber || '',
          tax_num: responses.taxNumber || '',
          incorporationDate: responses.incorporationDate || null,
        },
        contact: {
          ...state.contact,
          country: responses.country || '',
          email: responses.email || '',
          first_name: responses.firstName || '',
          last_name: responses.lastName || '',
          organization_id: responses.organizationId || '',
          phone_number: responses.phoneNumber || '',
        },
        quote: '', // Add quote property
        projectPeriodStart: '', // Add projectPeriodStart property
        projectPeriodEnd: '', // Add projectPeriodEnd property
      };
      console.log('insert form data:', data, responses, payload);
      actions.updateAction(payload);

      if (['dropout_before_purchase', 'quoted'].includes(quotedData.status)) {
        switch (data.providerCode) {
          case 'MS_AMLIN':
            amlinRedirect(['amlin_stg_10_confirm_information']);
            break;
          case 'CHUBB_UK':
            chubbRedirect(['confirm_information']);
            break;
          case 'HISCOX':
            hiscoxRedirect(['hiscox_stg_10_comfirm_information']);
            break;
          default:
            chubbRedirect(['confirm_information']);
            break;
        }
      } else {
        switch (data.providerCode) {
          case 'MS_AMLIN':
            amlinRedirect(data.stages);
            break;
          case 'CHUBB_UK':
            chubbRedirect(data.stages);
            break;
          case 'HISCOX':
            hiscoxRedirect(data.stages);
            break;
          default:
            chubbRedirect(data.stages);
            break;
        }
      }
    } catch (error) {
      console.log('init error: ', error);
    }
  };
  const chubbStagesToRoutes = {
    stg_1_business: `/${quoteId}/business-type`,
    stg_2_activity_bs: `/${quoteId}${AppRoutesPaths.Stg2}`,
    stg_2_activity_work: `/${quoteId}${AppRoutesPaths.Stg2}`,
    stg3_company_search: `/${quoteId}${AppRoutesPaths.Stg3}`,
    stg3_1_company_details: `/${quoteId}${AppRoutesPaths.Stg3}`,
    confirm_information: `/${quoteId}/confirm-information`,
  };
  const hiscoxStagesToRoutes = {
    stg_1_business: `/${quoteId}/business-type`,
    // ... stag 2 and 3 todo ...
    hiscox_stg_3_company_details: AppRoutesPaths.HiscoxCompanyDetails,
    hiscox_stg_4_business_address: AppRoutesPaths.HiscoxBusinessAddress,
    hiscox_stg_5_business_activities: AppRoutesPaths.HiscoxBusinessActivities,
    hiscox_stg_6_estimaaed_income: AppRoutesPaths.HiscoxEstimatedIncome,
    hiscox_stg_7_income_split: AppRoutesPaths.HiscoxIncomeSplit,
    hiscox_stg_8_customise_cover: AppRoutesPaths.HiscoxCustomizeCover,
    hiscox_stg_9_declarations: AppRoutesPaths.HiscoxDeclarations,
    hiscox_stg_10_comfirm_information: AppRoutesPaths.HiscoxConfirmInformation,
    hiscox_stg_11_payment: AppRoutesPaths.HiscoxPayment,
    hiscox_stg_12_send_email: AppRoutesPaths.HiscoxSuccess,
    hiscox_stg_13_success: AppRoutesPaths.HiscoxSuccess,
  };

  const amlinStagesToRoutes = {
    stg_1_business: `/${quoteId}/business-type`,
    stg_2_activity_bs: AppRoutesPaths.AmlinTechIndustry,
    amlin_stg_5_estimated_income: AppRoutesPaths.AmlinEstimatedIncome,
    amlin_stg_6_business_employees: AppRoutesPaths.AmlinBusinessEmployees,
    amlin_stg_7_income_split: AppRoutesPaths.AmlinIncomeSplit,
    amlin_stg_8_customise_cover: AppRoutesPaths.AmlinCustomizeCover,
    amlin_stg_9_declarations: AppRoutesPaths.AmlinDeclarations,
    amlin_stg_10_confirm_information: AppRoutesPaths.AmlinConfirmInformation,
    amlin_stg_11_payment: AppRoutesPaths.AmlinPayment,
    //  .. email | success page todo ...
  };

  const redirect = (stages: any[], stagesToRoutes: Record<string, string>) => {
    if (stages.length === 0) {
      router.push(QuoteProceessUrlPath.QuoteProcess_PersonalDetails);
    } else {
      const currentStep = stages[stages.length - 1];
      const route = Object.keys(stagesToRoutes).find((key) =>
        currentStep.includes(key)
      );
      if (route) {
        router.push(stagesToRoutes[route]);
      } else {
        console.log('No matching stage found for redirect', currentStep);
      }
    }
  };

  const hiscoxRedirect = (stages: any[]) =>
    redirect(stages, hiscoxStagesToRoutes);
  const amlinRedirect = (stages: any[]) =>
    redirect(stages, amlinStagesToRoutes);
  const chubbRedirect = (stages: any[]) =>
    redirect(stages, chubbStagesToRoutes);
  const queryQuoted = async () => {
    try {
      const res: any = await apiQueryQuote(quoteId as string); // Cast quoteId to string
      let result; // Declare the variable 'result' before using it
      if (res && 'payload' in res) {
        // Add type guard to check if res is not null
        result = await apiQueryRemainingResponse({
          quoteId: quoteId as string,
          qsetId: (res.payload as { qsetId: string }).qsetId, // Add type assertion
        });
        actions.updateAction({
          ...state,
          labels: res.payload.labels || [],
        });
        insertFormDataToLocal(result, res);
      }
    } catch (error) {
      console.log('queryQuoted error', error);
    }
  };
  useEffect(() => {
    console.log('quoteId', quoteId);
    queryQuoted();
  }, []);
}
